import { InboxItem } from "../aggregates/InboxItem";
import { InboxItemAggregateRepository } from "../../infrastructure/repositories/InboxItemAggregateRepository";

/**
 * Inbox Import/Export Domain Service
 * 
 * Following Eric Evans Domain Service pattern for operations that don't
 * naturally belong to any single aggregate but are part of the domain.
 * 
 * Import/Export is a legitimate GTD capability - not just technical migration.
 */
export class InboxImportExportService {
    constructor(
        private readonly repository: InboxItemAggregateRepository
    ) {}

    /**
     * Export all inbox items to a portable format
     */
    async exportItems(filters?: {
        status?: 'processed' | 'unprocessed';
        limit?: number;
    }): Promise<InboxExportData> {
        // Get items based on filters
        const items = await this.repository.findAll();

        // Filter by status if provided
        const filteredItems = filters?.status 
            ? items.filter((item: InboxItem) => item.processingStatus === filters.status)
            : items;

        // Apply limit
        const limitedItems = filters?.limit 
            ? filteredItems.slice(0, filters.limit)
            : filteredItems;

        return {
            exportDate: new Date().toISOString(),
            version: "1.0",
            source: "swoft-gtd-inbox",
            totalItems: limitedItems.length,
            items: limitedItems.map((item: InboxItem) => ({
                originalContent: item.originalContent,
                capturedAt: item.capturedAt.toISOString(),
                capturedByPersonId: item.capturedByPersonId,
                clarification: item.clarification || null,
                isActionable: item.isActionable || null,
                processingStatus: item.processingStatus as 'processed' | 'unprocessed'
            }))
        };
    }

    /**
     * Import items from external format
     * Supports migration from legacy formats
     */
    async importItems(
        importData: InboxImportData,
        options: {
            skipDuplicates?: boolean;
            capturedByPersonId?: string;
        } = {}
    ): Promise<InboxImportResult> {
        this.validateImportData(importData);

        const results: InboxImportResult = {
            totalItems: importData.items.length,
            imported: 0,
            skipped: 0,
            errors: []
        };

        for (const itemData of importData.items) {
            try {
                // Handle different input formats (legacy and new)
                const content = itemData.originalContent || itemData.text || itemData.content;
                if (!content) {
                    results.errors.push(`Item missing content: ${JSON.stringify(itemData)}`);
                    continue;
                }

                // Create domain aggregate
                const item = InboxItem.capture(
                    content,
                    options.capturedByPersonId || itemData.capturedByPersonId || 'import-system'
                );

                // Apply additional properties if available (for processed items)
                if (itemData.clarification && typeof itemData.isActionable === 'boolean') {
                    item.clarify(itemData.clarification, itemData.isActionable, 'import-system');
                }

                // Persist via repository
                await this.repository.save(item);
                results.imported++;

            } catch (error) {
                if (options.skipDuplicates && error instanceof Error && error.message.includes('duplicate')) {
                    results.skipped++;
                } else {
                    results.errors.push(`Failed to import item: ${error instanceof Error ? error.message : 'Unknown error'}`);
                }
            }
        }

        return results;
    }

    private validateImportData(data: InboxImportData): void {
        if (!data.version) {
            throw new Error('Import data missing version');
        }
        if (!data.items || !Array.isArray(data.items)) {
            throw new Error('Import data missing or invalid items array');
        }
        if (data.items.length === 0) {
            throw new Error('Import data contains no items');
        }
    }
}

// ============================================
// TYPES
// ============================================

export interface InboxExportData {
    exportDate: string;
    version: string;
    source: string;
    totalItems: number;
    items: ExportedInboxItem[];
}

export interface ExportedInboxItem {
    originalContent: string;
    capturedAt: string;
    capturedByPersonId: string;
    clarification: string | null;
    isActionable: boolean | null;
    processingStatus: 'processed' | 'unprocessed';
}

export interface InboxImportData {
    version: string;
    items: ImportedInboxItem[];
    source?: string;
    exportDate?: string;
}

export interface ImportedInboxItem {
    // New format
    originalContent?: string;
    capturedByPersonId?: string;
    clarification?: string | null;
    isActionable?: boolean | null;
    processingStatus?: 'processed' | 'unprocessed';
    
    // Legacy format support
    text?: string;
    content?: string;
    status?: string;
    priority?: string;
    tags?: string[];
    
    // Metadata
    capturedAt?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface InboxImportResult {
    totalItems: number;
    imported: number;
    skipped: number;
    errors: string[];
}